import type React from "react";
import { X } from "lucide-react";
import { i18n } from "@frontend/i18n";
import { EstimatedReadTimeService } from "../../../domain/services/estimated-read-time.service";

interface TopPicksBannerProps {
  readonly onDismiss: () => void;
  readonly onClick: () => void;
  readonly totalProfilesCount?: number | undefined;
}

export function TopPicksBanner({
  onDismiss,
  onClick,
  totalProfilesCount,
}: TopPicksBannerProps): React.JSX.Element {
  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick();
    }
  };

  // The dismiss control is a sibling of the entry button (not nested) — an
  // interactive element inside a role="button" is invalid interactive nesting,
  // and it made the wrapper's accessible name include the dismiss label.
  return (
    <div className="relative w-full">
      <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        className="w-full flex gap-3 p-4 pr-12 rounded-md bg-accent hover:bg-primary/15 transition-colors text-left cursor-pointer"
      >
        <div className="flex flex-col gap-0.5 flex-1 min-w-0">
          <span className="text-sm font-medium text-foreground leading-tight">
            {i18n.t("user.directory.topPicks.banner.title")}
          </span>
          {totalProfilesCount !== undefined && (
            <span className="text-sm text-muted-foreground leading-tight">
              {totalProfilesCount === 1
                ? i18n.t("user.directory.topPicks.banner.subtitleOne", {
                    time: EstimatedReadTimeService.format(totalProfilesCount),
                  })
                : i18n.t("user.directory.topPicks.banner.subtitle", {
                    count: totalProfilesCount,
                    time: EstimatedReadTimeService.format(totalProfilesCount),
                  })}
            </span>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={i18n.t("user.directory.topPicks.banner.dismiss")}
        className="absolute right-1 top-1 p-3 opacity-60 transition-opacity hover:opacity-100"
      >
        <X className="h-4 w-4 text-foreground" />
      </button>
    </div>
  );
}
